import React, { useEffect, useMemo, useState } from "react";
import { getDisplayPlatforms } from "../utils/networkNames";
import PropTypes from "prop-types";
import { CalendarDays, ExternalLink, Heart, Play, Share2 } from "lucide-react";
import SubpageHeader from "../components/SubpageHeader";
import SiteFooter from "../components/SiteFooter";
import UtilityPageIntro from "../components/UtilityPageIntro";
import Button from "../components/ui/Button";
import Chip from "../components/ui/Chip";
import RatingBadge from "../components/ui/RatingBadge";
import SeasonBadge from "../components/ui/SeasonBadge";
import Typography from "../components/ui/Typography";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorAlert from "../components/ErrorAlert";
import PosterPlaceholder from "../components/ui/PosterPlaceholder";
import ShowsLikeLink from "../components/ShowsLikeLink";
import useWatchlist from "../hooks/useWatchlist";
import usePageMeta from "../hooks/usePageMeta";
import { fetchBestOfReleases } from "../services/backendApi";
import { handleInternalLinkClick } from "../utils/navigation";
import { resolveInitialPayload } from "../utils/initialPayload";
import { buildAggregateRatingSchema, getPrimaryRating, normalizeRatingValue } from "../utils/showRatings";
import { buildBestOfSeoMeta } from "./best-of/bestOfSeo";
import { cn } from "../utils/classNames";
import { formatReleaseDate } from "../utils/formatReleaseDate";
import { formatDisplayDate } from "../utils/formatDisplayDate";
import { getContentRating } from "../utils/showFields";
import { shareShow } from "../utils/share";
import { trackEvent } from "../utils/analytics";
import { BUTTON_LABELS } from "../constants/buttonLabels";
import { getWatchlistToggleLabel } from "../utils/watchlist";

function getPrimaryNetwork(show) {
  return getDisplayPlatforms(show);
}

function getDisplayGenres(show, limit = 2) {
  return Array.isArray(show.genres) ? show.genres.filter(Boolean).slice(0, limit) : [];
}

function getTrailerUrl(show) {
  return show.trailers?.find((trailer) => trailer?.url)?.url || null;
}

function getStructuredDataImage(show) {
  return show?.posterMetadata?.thumbnail || show?.poster || show?.omdbData?.poster || null;
}

function getStructuredDataDescription(show) {
  return show?.plotOverview || show?.summary || show?.omdbData?.plot || "";
}

// The rating the badge shows: the month's frozen value, else the current one.
function getBestOfDisplayRating(show) {
  return normalizeRatingValue(show?.bestOfRating) ?? getPrimaryRating(show);
}

const MAJOR_AWARDS = [
  { pattern: /primetime emmys?/i, name: "Emmy" },
  { pattern: /golden globes?/i, name: "Golden Globe" },
  { pattern: /bafta/i, name: "BAFTA" },
];

// OMDb's lead clause for a major award: "Won 4 Primetime Emmys. 28 wins…" → "Won 4 Emmys".
function summarizeAwards(awards) {
  const match = /^(Won|Nominated for) (\d+) ([^.\d]+)/i.exec(String(awards || ""));
  const award = match && MAJOR_AWARDS.find(({ pattern }) => pattern.test(match[3]));
  if (!award) return "";
  const count = Number(match[2]);
  const plural = count === 1 ? "" : "s";
  return /^won/i.test(match[1])
    ? `Won ${count} ${award.name}${plural}`
    : `${count} ${award.name} nomination${plural}`;
}

// "September 2026" from "2026-09". The rating appears once per card, in its badge.
export function formatYearMonthLabel(yearMonth) {
  const [year, month] = String(yearMonth || "").split("-").map(Number);
  if (!year || !month) return String(yearMonth || "");
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
}

// A pick's reason is one sentence on what it's like (`whatItsLike`, built with the payload),
// plus any major award as data.
function BestOfReason({ show }) {
  const awards = summarizeAwards(show.omdbData?.awards);
  if (!show.whatItsLike && !awards) return null;
  return (
    <div className="space-y-1">
      {show.whatItsLike && (
        <Typography variant="body-small" className="reading-measure text-foreground">
          <span className="text-muted-foreground">What it&apos;s like: </span>
          {show.whatItsLike}
        </Typography>
      )}
      {awards && <Typography variant="body-small" className="text-muted-foreground">{awards}</Typography>}
    </div>
  );
}

BestOfReason.propTypes = { show: PropTypes.object.isRequired };

const BestOfActions = ({ show, isInWatchlist, onToggleWatchlist, compact = false }) => {
  const inWatchlist = isInWatchlist?.(show);
  const trailerUrl = getTrailerUrl(show);
  const showPath = show.slug ? `/show/${show.slug}` : null;

  return (
    <div className={cn("flex flex-wrap items-center gap-2", compact && "gap-1.5")}>
      {compact && <Button
        type="button"
        size="icon"
        variant="ghost"
        onClick={(event) => {
          event.stopPropagation();
          onToggleWatchlist?.(show);
        }}
        className={cn("sp-card-icon-action", inWatchlist && "sp-card-icon-action--active")}
        aria-label={getWatchlistToggleLabel(show.title)}
        aria-pressed={Boolean(inWatchlist)}
      >
        <Heart className={cn("h-4 w-4", inWatchlist && "fill-current text-primary")} aria-hidden="true" />
      </Button>}
      {compact && <Button
        type="button"
        size="icon"
        variant="ghost"
        onClick={(event) => {
          event.stopPropagation();
          shareShow(show);
        }}
        className="sp-card-icon-action"
        aria-label={`Share ${show.title}`}
      >
        <Share2 className="h-4 w-4" aria-hidden="true" />
      </Button>}
      {showPath && !compact && (
        <Button variant="wireframe" size="sm" asChild>
          <a href={showPath} onClick={(event) => handleInternalLinkClick(event, showPath)}>
            Details
          </a>
        </Button>
      )}
      {show.detailsUrl && !compact && (
        <Button
          type="button"
          variant="wireframe"
          size="sm"
          onClick={() => {
            trackEvent("outbound_click", { surface: "best_of_card", type: "details", slug: show.slug });
            window.open(show.detailsUrl, "_blank", "noopener,noreferrer");
          }}
        >
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          {BUTTON_LABELS.QUERY_IMDB}
        </Button>
      )}
      {trailerUrl && !compact && (
        <Button
          type="button"
          variant="wireframe"
          size="sm"
          onClick={() => {
            trackEvent("outbound_click", { surface: "best_of_card", type: "trailer", slug: show.slug });
            window.open(trailerUrl, "_blank", "noopener,noreferrer");
          }}
        >
          <Play className="h-3.5 w-3.5" aria-hidden="true" />
          {BUTTON_LABELS.WATCH_TRAILER}
        </Button>
      )}
    </div>
  );
};

BestOfActions.propTypes = {
  show: PropTypes.object.isRequired,
  isInWatchlist: PropTypes.func,
  onToggleWatchlist: PropTypes.func,
  compact: PropTypes.bool,
};

const BestOfPoster = ({ show, priority = false, className = "" }) => {
  const showPath = show.slug ? `/show/${show.slug}` : null;

  const content = show.poster ? (
    <img
      src={show.poster}
      srcSet={show.posterMetadata?.thumbnail ? `${show.posterMetadata.thumbnail} 340w, ${show.poster} 680w` : undefined}
      sizes="(max-width: 768px) 34vw, 340px"
      alt={`${show.title} poster`}
      className="h-full w-full object-cover"
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : "auto"}
    />
  ) : (
    <PosterPlaceholder showId={show.id} className="h-full w-full bg-poster-placeholder-gradient" />
  );

  return (
    <div className={cn("overflow-hidden rounded-lg border border-accent-gold/65 bg-muted shadow-card-base", className)}>
      {showPath ? (
        <a
          href={showPath}
          onClick={(event) => handleInternalLinkClick(event, showPath)}
          className="block h-full focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`View details for ${show.title}`}
        >
          {content}
        </a>
      ) : content}
    </div>
  );
};

BestOfPoster.propTypes = {
  show: PropTypes.object.isRequired,
  priority: PropTypes.bool,
  className: PropTypes.string,
};

const BestOfMeta = ({ show, compact = false }) => {
  const primaryRating = getBestOfDisplayRating(show);
  const contentRating = getContentRating(show);
  const network = getPrimaryNetwork(show);
  const genres = getDisplayGenres(show, compact ? 1 : 2);

  return (
    <div className="flex flex-wrap items-center gap-2 text-muted-foreground">
      {primaryRating !== null ? (
        <RatingBadge variant="rating">{primaryRating.toFixed(1)}</RatingBadge>
      ) : (
        <RatingBadge variant="unrated">N/A</RatingBadge>
      )}
      <SeasonBadge seasonNumber={show.seasonNumber} part={show.seasonPart} variant={compact ? "compact" : "default"} />
      {contentRating && <Chip variant="outline" size="badge" className="text-meta">{contentRating}</Chip>}
      <span className="inline-flex items-center gap-1 text-caption text-meta text-muted-foreground">
        <CalendarDays className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
        {formatReleaseDate(show.releaseDate)}
      </span>
      {(network || genres.length > 0) && (
        <span className="min-w-0 text-caption text-muted-foreground">
          {[network, ...genres].filter(Boolean).join(" / ")}
        </span>
      )}
      <ShowsLikeLink show={show} surface="best_of_card" className="basis-full" />
    </div>
  );
};

BestOfMeta.propTypes = {
  show: PropTypes.object.isRequired,
  compact: PropTypes.bool,
};

const BestOfFeatureCard = ({ show, rank, isInWatchlist, onToggleWatchlist }) => {
  const showPath = show.slug ? `/show/${show.slug}` : null;

  return (
    <article
      className={cn(
        "grid grid-cols-[5.5rem_minmax(0,1fr)] gap-4 rounded-xl border border-accent-gold/55 bg-card/85 p-4 shadow-card-base sm:grid-cols-[9rem_minmax(0,1fr)] md:gap-6 md:p-5 lg:p-6",
        rank === 1
          ? "md:grid-cols-[minmax(180px,260px)_minmax(0,1fr)]"
          : "md:grid-cols-[9rem_minmax(0,1fr)]"
      )}
    >
      <BestOfPoster show={show} priority={rank === 1} className="aspect-[2/3] self-start md:max-h-none" />
      <div className="flex min-w-0 flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <div className="flex min-w-0 basis-full items-baseline gap-3 sm:basis-0 sm:flex-1">
            <span className="shrink-0 text-caption text-meta text-primary">#{rank}</span>
            <Typography as="h2" variant={rank === 1 ? "h1" : "h3"} className={cn("min-w-0 break-words text-foreground", rank === 1 && "max-sm:text-h2")}>
              {showPath ? (
                <a href={showPath} onClick={(event) => handleInternalLinkClick(event, showPath)} className="title-link">
                  {show.title}
                </a>
              ) : show.title}
            </Typography>
          </div>
          <BestOfActions show={show} isInWatchlist={isInWatchlist} onToggleWatchlist={onToggleWatchlist} compact />
        </div>

        <BestOfMeta show={show} />

        {show.plotOverview && (
          <Typography variant="body-small" className="reading-measure line-clamp-3 text-muted-foreground">
            {show.plotOverview}
          </Typography>
        )}

        <BestOfReason show={show} />

        <div className="mt-auto hidden sm:block">
          <BestOfActions show={show} />
        </div>
      </div>
    </article>
  );
};

BestOfFeatureCard.propTypes = {
  show: PropTypes.object.isRequired,
  rank: PropTypes.number.isRequired,
  isInWatchlist: PropTypes.func,
  onToggleWatchlist: PropTypes.func,
};

const BestOfRankedRow = ({ show, rank, isInWatchlist, onToggleWatchlist }) => {
  const showPath = show.slug ? `/show/${show.slug}` : null;

  return (
    <article className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-3 rounded-lg border border-accent-gold/45 bg-card/70 p-3 shadow-card-base transition-shadow hover:shadow-card-hover sm:grid-cols-[6rem_minmax(0,1fr)] sm:gap-4 sm:p-4 lg:grid-cols-[7.5rem_minmax(0,1fr)]">
      <BestOfPoster show={show} className="aspect-[2/3] self-start" />
      <div className="min-w-0 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
          <div className="flex min-w-0 basis-full gap-3 sm:basis-0 sm:flex-1">
            <span className="shrink-0 text-caption text-meta text-primary">#{rank}</span>
            <div className="min-w-0 space-y-1">
              <Typography as="h3" variant="h3" className="line-clamp-2 text-foreground">
                {showPath ? (
                  <a href={showPath} onClick={(event) => handleInternalLinkClick(event, showPath)} className="title-link">
                    {show.title}
                  </a>
                ) : show.title}
              </Typography>
              <BestOfMeta show={show} compact />
            </div>
          </div>
          <BestOfActions show={show} isInWatchlist={isInWatchlist} onToggleWatchlist={onToggleWatchlist} compact />
        </div>

        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
          <BestOfReason show={show} />
          {showPath && (
            <Button variant="wireframe" size="sm" className="sm:col-start-2" asChild>
              <a href={showPath} onClick={(event) => handleInternalLinkClick(event, showPath)}>
                Details
              </a>
            </Button>
          )}
        </div>
      </div>
    </article>
  );
};

BestOfRankedRow.propTypes = {
  show: PropTypes.object.isRequired,
  rank: PropTypes.number.isRequired,
  isInWatchlist: PropTypes.func,
  onToggleWatchlist: PropTypes.func,
};

const BestOfRankedList = ({ shows, isInWatchlist, onToggleWatchlist }) => {
  if (!shows.length) {
    return (
      <div className="rounded-xl border border-accent-gold/45 bg-card/75 p-8 text-center shadow-card-base">
        <Typography as="h2" variant="h3" className="text-foreground">No shortlist yet</Typography>
        <Typography variant="body" className="mx-auto mt-2 max-w-lg text-muted-foreground">
          This month does not have enough rated premieres for a useful best-of list yet. Browse the archive for older ranked months.
        </Typography>
      </div>
    );
  }

  const [topPick, ...rest] = shows;
  const supporting = rest.slice(0, 2);
  const remaining = rest.slice(2);

  return (
    <section className="space-y-6" aria-label="Ranked best-of shortlist">
      <BestOfFeatureCard
        show={topPick}
        rank={1}
        isInWatchlist={isInWatchlist}
        onToggleWatchlist={onToggleWatchlist}
      />

      {supporting.length > 0 && (
        <div className="grid gap-5 lg:grid-cols-2">
          {supporting.map((show, index) => (
            <BestOfFeatureCard
              key={show.id}
              show={show}
              rank={index + 2}
              isInWatchlist={isInWatchlist}
              onToggleWatchlist={onToggleWatchlist}
            />
          ))}
        </div>
      )}

      {remaining.length > 0 && (
        <div className="space-y-3">
          <Typography as="h2" variant="h3" className="text-foreground">
            The rest of the shortlist
          </Typography>
          <div className="space-y-3">
            {remaining.map((show, index) => (
              <BestOfRankedRow
                key={show.id}
                show={show}
                rank={index + 4}
                isInWatchlist={isInWatchlist}
                onToggleWatchlist={onToggleWatchlist}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

BestOfRankedList.propTypes = {
  shows: PropTypes.arrayOf(PropTypes.object).isRequired,
  isInWatchlist: PropTypes.func,
  onToggleWatchlist: PropTypes.func,
};

const BestOfStructuredData = ({ yearMonth, label, shows, summary = "", lastUpdated = null }) => {
  if (!Array.isArray(shows) || shows.length === 0) return null;

  const pageUrl = `https://premierelens.com/best-of/${yearMonth}`;
  const articleJson = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `Best New and Returning TV of ${label}`,
    description: summary || `Top rated TV premieres curated by PremiereLens for ${label}`,
    url: pageUrl,
    mainEntityOfPage: pageUrl,
    author: {
      "@type": "Organization",
      name: "PremiereLens",
      url: "https://premierelens.com/about",
    },
    publisher: {
      "@type": "Organization",
      name: "PremiereLens",
      url: "https://premierelens.com",
      logo: {
        "@type": "ImageObject",
        url: "https://premierelens.com/favicon-v2.png",
      },
    },
    ...(lastUpdated ? { dateModified: lastUpdated } : {}),
  };

  const itemListJson = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `Best New and Returning TV of ${label}`,
    description: `Top rated TV premieres curated by PremiereLens for ${label}`,
    url: pageUrl,
    numberOfItems: shows.length,
    itemListElement: shows.map((show, index) => {
      const aggregateRating = buildAggregateRatingSchema(show);
      const image = getStructuredDataImage(show);
      const description = getStructuredDataDescription(show);

      return {
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "TVSeries",
          name: show.title,
          url: `https://premierelens.com/show/${show.slug}`,
          ...(image ? { image } : {}),
          ...(description ? { description } : {}),
          ...(aggregateRating ? { aggregateRating } : {}),
        },
      };
    }),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify([articleJson, itemListJson]) }}
    />
  );
};

BestOfStructuredData.propTypes = {
  yearMonth: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  shows: PropTypes.arrayOf(PropTypes.object),
  summary: PropTypes.string,
  lastUpdated: PropTypes.string,
};

const BestOfPage = ({ yearMonth, initialBestOfPayload = null }) => {
  const initialPayload = useMemo(
    () => resolveInitialPayload(initialBestOfPayload, "yearMonth", yearMonth),
    [yearMonth, initialBestOfPayload]
  );

  const [payload, setPayload] = useState(() => initialPayload || null);
  const [loading, setLoading] = useState(() => !initialPayload);
  const [error, setError] = useState("");
  const { isInWatchlist, toggleWatchlist, upsertWatchlistEntries } = useWatchlist();

  useEffect(() => {
    if (initialPayload) return;
    let active = true;
    const run = async () => {
      try {
        setLoading(true);
        const nextPayload = await fetchBestOfReleases(yearMonth);
        if (!active) return;
        setPayload(nextPayload);
        setError("");
      } catch (nextError) {
        if (!active) return;
        setError(nextError.message || "Failed to load best-of list");
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [yearMonth, initialPayload]);

  const shows = useMemo(() => (Array.isArray(payload?.data) ? payload.data : []), [payload?.data]);
  const label = payload?.label || yearMonth;
  const seoMeta = buildBestOfSeoMeta(yearMonth, {
    count: shows.length,
    topShows: shows.map((show) => show.title).filter(Boolean),
  });
  const previousYearMonth = payload?.previousYearMonth || null;
  const nextYearMonth = payload?.nextYearMonth || null;
  const monthPath = `/month/${yearMonth}`;
  const ratingMode = payload?.ratingMode === "snapshot" ? "snapshot" : "current";
  const rankingCopy = shows.length
    ? ratingMode === "snapshot"
      ? `Ranked by rating when the month closed: IMDb's, or the viewer rating from the release listings where IMDb has none. Release date breaks ties.`
      : `Ranked by current rating: IMDb's, or the viewer rating from the release listings where IMDb has none. Release date breaks ties. Updated ${formatDisplayDate(payload?.lastUpdated) || "as new data lands"}.`
    : `A best-of list appears once ${label} has enough rated premieres to judge fairly.`;

  usePageMeta({
    title: seoMeta.title,
    description: seoMeta.description,
    pathname: `/best-of/${yearMonth}`,
    ogType: seoMeta.ogType,
  });

  useEffect(() => {
    upsertWatchlistEntries(shows);
  }, [shows, upsertWatchlistEntries]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SubpageHeader backHref="/" backLabel="Home" />
      <BestOfStructuredData
        yearMonth={yearMonth}
        label={label}
        shows={shows}
        summary={payload?.editorialSummary || seoMeta.description}
        lastUpdated={payload?.lastUpdated || null}
      />
      <main id="main-content" tabIndex={-1} className="route-shell route-shell--standard">
        <div className="mb-8">
          <UtilityPageIntro
            title={`Best New and Returning TV of ${label}`}
            description={payload?.editorialSummary || `The highest-rated new and returning scripted shows for ${label}.`}
            className="best-of-intro"
            actions={(
              <Button variant="outline" asChild>
                <a href={monthPath} onClick={(event) => handleInternalLinkClick(event, monthPath)}>
                  All {label} premieres
                </a>
              </Button>
            )}
          >
            <div className="grid gap-1">
              <Typography variant="body-small" className="max-w-3xl text-muted-foreground">
                By PremiereLens
              </Typography>
              <Typography variant="body-small" className="max-w-3xl text-muted-foreground">
                {rankingCopy}
              </Typography>
            </div>
          </UtilityPageIntro>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="max-w-2xl mx-auto">
            <ErrorAlert message={error} />
          </div>
        ) : (
          <BestOfRankedList
            shows={shows}
            isInWatchlist={isInWatchlist}
            onToggleWatchlist={toggleWatchlist}
          />
        )}

        {!loading && !error && (
          <nav aria-label="More from the archive" className="mt-10 space-y-3 border-t border-border pt-6">
            <Typography as="h2" variant="h3" className="text-foreground">Beyond the top ten</Typography>
            <Typography variant="body-small" className="reading-measure text-muted-foreground">
              Every premiere from {label} is on its month page, with filters and sorting.
            </Typography>
            <div className="flex flex-wrap gap-2">
              <Button asChild>
                <a href={monthPath} onClick={(event) => handleInternalLinkClick(event, monthPath)}>
                  See all {label} premieres
                </a>
              </Button>
              {previousYearMonth && (
                <Button variant="outline" asChild>
                  <a
                    href={`/best-of/${previousYearMonth}`}
                    onClick={(event) => handleInternalLinkClick(event, `/best-of/${previousYearMonth}`)}
                  >
                    Previous: {formatYearMonthLabel(previousYearMonth)}
                  </a>
                </Button>
              )}
              {nextYearMonth && (
                <Button variant="outline" asChild>
                  <a
                    href={`/best-of/${nextYearMonth}`}
                    onClick={(event) => handleInternalLinkClick(event, `/best-of/${nextYearMonth}`)}
                  >
                    Next: {formatYearMonthLabel(nextYearMonth)}
                  </a>
                </Button>
              )}
              <Button variant="outline" asChild>
                <a href="/archive" onClick={(event) => handleInternalLinkClick(event, "/archive")}>
                  Browse archive
                </a>
              </Button>
            </div>
          </nav>
        )}
      </main>
      <SiteFooter />
    </div>
  );
};

BestOfPage.propTypes = {
  yearMonth: PropTypes.string.isRequired,
  initialBestOfPayload: PropTypes.shape({
    yearMonth: PropTypes.string,
    label: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.object),
    count: PropTypes.number,
    ratingMode: PropTypes.oneOf(["current", "snapshot"]),
    editorialSummary: PropTypes.string,
    previousYearMonth: PropTypes.string,
    nextYearMonth: PropTypes.string,
    lastUpdated: PropTypes.string,
  }),
};

export default BestOfPage;
